import React, { useState, useEffect, useRef } from 'react';
import { Plus, Printer, Download, Eye, Upload } from 'lucide-react';
import { invoiceAPI, customerAPI, productAPI, parseFileData } from '../utils/api';
import { generateInvoicePDF, downloadPDF } from '../utils/exportUtils';
import InvoiceForm from '../components/InvoiceForm';
import InvoiceTypeModal from '../components/InvoiceTypeModal';
import InvoiceViewModal from '../components/InvoiceViewModal';
import CustomerModal from '../components/CustomerModal';
import '../styles/Invoices.css';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showInvoiceTypeModal, setShowInvoiceTypeModal] = useState(false);
  const [showViewInvoiceModal, setShowViewInvoiceModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceType, setInvoiceType] = useState('with-gst');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [alert, setAlert] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  useEffect(() => {
    fetchData();
    // Listen for invoice type modal event from sidebar
    const handleOpenTypeModal = () => {
      setShowInvoiceTypeModal(true);
    };
    window.addEventListener('openInvoiceTypeModal', handleOpenTypeModal);
    return () => window.removeEventListener('openInvoiceTypeModal', handleOpenTypeModal);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, customersRes, productsRes] = await Promise.all([
        invoiceAPI.getAll(),
        customerAPI.getAll(),
        productAPI.getAll(),
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      showAlert('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async (invoiceData) => {
    try {
      await invoiceAPI.create(invoiceData);
      showAlert('Invoice created successfully', 'success');
      fetchData();
      setShowForm(false);
      setShowInvoiceTypeModal(false);
    } catch (error) {
      showAlert('Error creating invoice', 'error');
    }
  };

  const handleAddCustomer = () => {
    setShowCustomerModal(true);
  };

  const handleSaveCustomer = async (formData) => {
    try {
      await customerAPI.create(formData);
      showAlert('Customer created successfully', 'success');

      const customersRes = await customerAPI.getAll();
      setCustomers(customersRes.data);
      setShowCustomerModal(false);
    } catch (error) {
      showAlert('Error saving customer', 'error');
    }
  };

  const handleSelectInvoiceType = (type) => {
    setInvoiceType(type);
    setShowInvoiceTypeModal(false);
    setShowForm(true);
  };

  const handlePrintInvoice = async (invoiceId) => {
    try {
      const response = await invoiceAPI.getById(invoiceId);
      const invoice = response.data;
      const customer = customers.find((c) => c.id === invoice.customerId);
      
      if (customer) {
        const doc = generateInvoicePDF(invoice, customer);
        window.print();
      }
    } catch (error) {
      showAlert('Error preparing invoice for print', 'error');
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await invoiceAPI.getById(invoiceId);
      const invoice = response.data;
      const customer = customers.find((c) => c.id === invoice.customerId);
      
      if (customer) {
        const doc = generateInvoicePDF(invoice, customer);
        downloadPDF(doc, `Invoice-${invoice.invoiceNumber}.pdf`);
      }
    } catch (error) {
      showAlert('Error downloading PDF', 'error');
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const response = await invoiceAPI.getById(invoiceId);
      setSelectedInvoice(response.data);
      setSelectedInvoiceId(invoiceId);
      setShowViewInvoiceModal(true);
    } catch (error) {
      showAlert('Error loading invoice details', 'error');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const fileInputRef = useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const invoices = await parseFileData(file);
      await invoiceAPI.bulkCreate(invoices);
      showAlert(`Successfully imported ${invoices.length} invoices`, 'success');
      fetchData();
    } catch (error) {
      showAlert('Error importing invoices: ' + error.message, 'error');
    } finally {
      setLoading(false);
      event.target.value = ''; // Reset file input
    };
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Invoices</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => fileInputRef.current.click()}>
            <Upload size={18} />
            Import CSV/Excel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button className="btn btn-primary" onClick={() => setShowInvoiceTypeModal(true)}>
            <Plus size={18} />
            New Invoice
          </button>
        </div>
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}

      {/* INVOICES SECTION */}
      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="empty-state">
            <p>No invoices created yet</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const customer = customers.find((c) => c.id === invoice.customerId);
                  return (
                    <tr key={invoice.id}>
                      <td>{invoice.invoiceNumber}</td>
                      <td>{customer?.fullName || 'N/A'}</td>
                      <td>{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                      <td>₹{Number(invoice.grandTotal || 0).toFixed(2)}</td>
                      <td>
                        <span className={`badge badge-${invoice.status}`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="action-buttons">
                        <button
                          className="btn-icon"
                          onClick={() => handleViewInvoice(invoice.id)}
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handlePrintInvoice(invoice.id)}
                          title="Print"
                        >
                          <Printer size={16} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDownloadPDF(invoice.id)}
                          title="Download PDF"
                        >
                          <Download size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <InvoiceForm
          customers={customers}
          products={products}
          onSave={handleCreateInvoice}
          onClose={() => setShowForm(false)}
          onAddCustomer={handleAddCustomer}
          invoiceType={invoiceType}
        />
      )}

      {showCustomerModal && (
        <CustomerModal
          customer={null}
          onSave={handleSaveCustomer}
          onClose={() => setShowCustomerModal(false)}
        />
      )}

      {showInvoiceTypeModal && (
        <InvoiceTypeModal
          onSelectType={handleSelectInvoiceType}
          onClose={() => setShowInvoiceTypeModal(false)}
        />
      )}

      {showViewInvoiceModal && selectedInvoice && (
        <InvoiceViewModal
          invoice={selectedInvoice}
          customer={customers.find(c => c.id === selectedInvoice.customerId)}
          onClose={() => {
            setShowViewInvoiceModal(false);
            setSelectedInvoice(null);
            setSelectedInvoiceId(null);
          }}
          onPrint={() => handlePrintInvoice(selectedInvoiceId)}
          onDownloadPDF={() => handleDownloadPDF(selectedInvoiceId)}
        />
      )}
    </div>
  );
}

export default Invoices;
